import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { ArrowLeft } from 'lucide-react'
import { contentApi, Portion, Domain, Mastery } from '../../api/content'
import { assignmentsApi } from '../../api/assignments'
import { plansApi } from '../../api/plans'
import { questionsApi, Question } from '../../api/questions'
import { useAuth } from '../../context/AuthContext'

export function AssignmentCreate() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const insertPlanIdParam = searchParams.get('insertPlanId')
  const insertPositionParam = searchParams.get('insertPosition')
  const insertAppendParam = searchParams.get('append') === '1'
  const insertPlanId = insertPlanIdParam ? Number(insertPlanIdParam) : null
  const insertPosition = insertPositionParam ? Number(insertPositionParam) : null
  /** Tutor: plan + slot. Student: plan + append, or plan + explicit position. */
  const isInsertMode =
    insertPlanId !== null && (insertAppendParam || insertPosition !== null)
  const isStudentSelfInsert = user?.role === 'student' && isInsertMode

  const [loadingContent, setLoadingContent] = useState(true)
  const [portions, setPortions] = useState<Portion[]>([])
  const [domains, setDomains] = useState<Domain[]>([])
  const [masteries, setMasteries] = useState<Mastery[]>([])
  const [masteryQuestions, setMasteryQuestions] = useState<Question[]>([])
  const [loadingQuestions, setLoadingQuestions] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      assignment_type: 'learning',
      grading_mode: 'auto',
      base_xp: 10,
      due_date: '',
      portion_id: '',
      domain_id: '',
      mastery_id: '',
      difficulty_level: '',
      question_count: 10,
    },
    validate: {
      title: (v) => (v.trim() ? null : 'Title is required'),
      base_xp: (v) => (v > 0 ? null : 'XP must be positive'),
      portion_id: (v) => (v ? null : 'Select a Subject'),
      question_count: (v, values) => {
        if (v <= 0) return 'Question count must be greater than zero'
        if (!values.mastery_id) return null  // can't validate without mastery
        const available = values.difficulty_level
          ? masteryQuestions.filter(
              (question) => String(question.difficulty_level) === values.difficulty_level
            ).length
          : masteryQuestions.length
        if (available > 0 && v > available) {
          return `Only ${available} question(s) are available for this mastery and difficulty`
        }
        return null
      },
    },
  })

  useEffect(() => {
    if (user?.role === 'student' && !isInsertMode) {
      navigate('/study-plan', { replace: true })
    }
  }, [user?.role, isInsertMode, navigate])

  useEffect(() => {
    Promise.all([contentApi.getPortions()])
      .then(([portionsRes]) => {
        setPortions(portionsRes.data)
      })
      .catch(() => {
        notifications.show({ title: 'Error', message: 'Failed to load SAT content.', color: 'red' })
      })
      .finally(() => setLoadingContent(false))
  }, [])

  const selectedPortionName = useMemo(
    () => portions.find((portion) => String(portion.id) === form.values.portion_id)?.name ?? '—',
    [form.values.portion_id, portions]
  )

  const selectedDomainName = useMemo(
    () => domains.find((domain) => String(domain.id) === form.values.domain_id)?.name ?? '—',
    [domains, form.values.domain_id]
  )

  const selectedMasteryName = useMemo(
    () => masteries.find((mastery) => String(mastery.id) === form.values.mastery_id)?.name ?? '—',
    [form.values.mastery_id, masteries]
  )

  const handlePortionChange = async (value: string | null) => {
    form.setFieldValue('portion_id', value || '')
    form.setFieldValue('domain_id', '')
    form.setFieldValue('mastery_id', '')
    form.setFieldValue('question_count', 10)
    setDomains([])
    setMasteries([])
    setMasteryQuestions([])

    if (!value) {
      return
    }

    try {
      const res = await contentApi.getDomains(Number(value))
      setDomains(res.data)
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load domains.', color: 'red' })
    }
  }

  const handleDomainChange = async (value: string | null) => {
    form.setFieldValue('domain_id', value || '')
    form.setFieldValue('mastery_id', '')
    form.setFieldValue('question_count', 10)
    setMasteries([])
    setMasteryQuestions([])

    if (!value) {
      return
    }

    try {
      const res = await contentApi.getMasteries(Number(value))
      setMasteries(res.data)
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load masteries.', color: 'red' })
    }
  }

  const handleMasteryChange = async (value: string | null) => {
    form.setFieldValue('mastery_id', value || '')
    form.setFieldValue('question_count', 10)
    setMasteryQuestions([])

    if (!value) {
      return
    }

    setLoadingQuestions(true)
    try {
      const res = await questionsApi.list(Number(value))
      setMasteryQuestions(res.data)
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load questions for this mastery.', color: 'red' })
    } finally {
      setLoadingQuestions(false)
    }
  }

  const availableQuestionCount = useMemo(
    () => form.values.difficulty_level
      ? masteryQuestions.filter(
          (question) => String(question.difficulty_level) === form.values.difficulty_level
        ).length
      : masteryQuestions.length,
    [form.values.difficulty_level, masteryQuestions]
  )

  useEffect(() => {
    if (!form.values.mastery_id) {
      return
    }
    if (availableQuestionCount === 0) {
      form.setFieldValue('question_count', 1)
      return
    }
    if (form.values.question_count > availableQuestionCount) {
      form.setFieldValue('question_count', availableQuestionCount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableQuestionCount, form.values.mastery_id])

  const handleSubmit = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      return
    }

    setSubmitting(true)
    try {
      const vals = form.values
      const basePayload = {
        title: vals.title,
        description: vals.description || undefined,
        // The dropdown drives both fields: `action` controls student-side
        // routing (StudyPlan.tsx::buildRoute) while assignment_type stays
        // for legacy backward compatibility.
        action: vals.assignment_type,
        assignment_type: vals.assignment_type,
        grading_mode: vals.grading_mode as 'auto' | 'manual',
        base_xp: vals.base_xp,
        due_date: vals.due_date || null,
        portion_id: Number(vals.portion_id),
        domain_id: vals.domain_id ? Number(vals.domain_id) : undefined,
        mastery_id: vals.mastery_id ? Number(vals.mastery_id) : undefined,
        difficulty_level: vals.difficulty_level ? Number(vals.difficulty_level) : undefined,
        question_count: vals.question_count,
      }

      // Single POST /assignments: create, assign to self, and insert plan session (backend).
      if (isStudentSelfInsert && insertPlanId !== null) {
        await assignmentsApi.create({
          ...basePayload,
          insert_plan_id: insertPlanId,
          ...(insertAppendParam
            ? { insert_append: true }
            : { insert_position: insertPosition! }),
        })
        notifications.show({
          title: 'Task added',
          message: 'Your assignment was added to your study plan.',
          color: 'green',
        })
        navigate('/study-plan')
        return
      }

      const res = await assignmentsApi.create(basePayload)

      // If launched from PlanMgr's "+" button, we need to:
      //   1. Auto-assign the new assignment to the plan's student so it
      //      shows up in StudyPlan (which reads tbl_assignment_student).
      //   2. Insert it as a new session into the plan at the requested
      //      position. The backend shifts display_order for any sessions
      //      at or below `position`.
      if (
        !isStudentSelfInsert
        && isInsertMode
        && insertPlanId !== null
        && insertPosition !== null
      ) {
        try {
          const planMetaRes = await plansApi.getPlanMeta(insertPlanId)
          const studentId = planMetaRes.data.student_id
          await assignmentsApi.assign(res.data.id, { student_ids: [studentId] })

          await plansApi.insertSession(insertPlanId, {
            position: insertPosition,
            title: vals.title,
            description: vals.description || null,
            status: 'assigned',
          })
          notifications.show({
            title: 'Session inserted',
            message: `Assignment created, assigned to the student, and inserted at position ${insertPosition}.`,
            color: 'green',
          })
          navigate(`/tutor/plans/${insertPlanId}`)
          return
        } catch (insertErr: any) {
          // Assignment was created, but a follow-up step failed.
          // Surface the error and fall through to the normal post-create
          // flow so the tutor doesn't lose track of the new assignment.
          notifications.show({
            title: 'Assignment created, but follow-up failed',
            message:
              insertErr?.response?.data?.detail ||
              'Could not assign the new session to the plan. Opening assignment detail instead.',
            color: 'orange',
          })
          navigate(`/assignments/${res.data.id}`)
          return
        }
      }

      notifications.show({
        title: 'Assignment created',
        message: 'Now choose a roster or selected students on the detail page.',
        color: 'green',
      })
      navigate(`/assignments/${res.data.id}`)
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to create assignment.',
        color: 'red',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (loadingContent) {
    return (
      <Container size="sm" py={48}>
        <Group justify="center">
          <Loader color="eveningSea" />
        </Group>
      </Container>
    )
  }

  const cancelHref =
    isInsertMode && insertPlanId !== null
      ? user?.role === 'student'
        ? '/study-plan'
        : `/tutor/plans/${insertPlanId}`
      : user?.role === 'student'
        ? '/study-plan'
        : '/tutor/assignments'

  return (
    <Container size="lg" py={32}>
      <Group mb={24}>
        <ActionIcon variant="subtle" color="gray" onClick={() => navigate(cancelHref)}>
          <ArrowLeft size={20} />
        </ActionIcon>
        <Title order={2} c="var(--sat-text)">Create Assignment</Title>
      </Group>

      {isInsertMode && (
        <Alert color="blue" mb={16}>
          {user?.role === 'student' && insertAppendParam ? (
            <>
              This assignment will be appended as a new session at the end of your study plan
              timeline.
            </>
          ) : user?.role === 'student' && insertPosition != null ? (
            <>
              This assignment will be inserted as a new session at position{' '}
              <strong>{insertPosition}</strong> in the plan.
            </>
          ) : (
            <>
              You&apos;re creating an assignment that will be inserted as a new session at
              position <strong>{insertPosition}</strong> in the plan. Existing sessions at
              or below that position will be shifted down by one.
            </>
          )}
        </Alert>
      )}

      <Stack gap={24}>
        <Paper shadow="sm" radius="md" p="xl">
          <Stack gap={16}>
            <div>
              <Title order={4} c="var(--sat-text)">Assignment Details</Title>
              <Text size="sm" c="var(--sat-text-soft)">
                {isStudentSelfInsert
                  ? 'This task is added to your study plan and appears in your assignment list.'
                  : 'Create the assignment first, then assign it to a whole roster or selected students.'}
              </Text>
            </div>

            <TextInput
              label="Title"
              placeholder="e.g. Algebra mastery drill"
              required
              {...form.getInputProps('title')}
            />

            <Textarea
              label="Description"
              placeholder="Optional notes for students"
              rows={3}
              {...form.getInputProps('description')}
            />

            <Group grow align="flex-start">
              <Select
                label="Assignment type"
                data={[
                  
                  { value: 'review', label: 'Review' }, 
                  { value: 'enhance', label: 'Enhance' },  
                  { value: 'quiz', label: 'Quiz' },
/*             { value: 'learning', label: 'Learning' },      
 { value: 'vocabulary', label: 'Vocabulary' },  
                { value: 'meeting', label: 'Meeting' },  
                   { value: 'mock', label: 'Mock' },
                  { value: 'evaluate', label: 'Evaluate' }, */
                ]}
                {...form.getInputProps('assignment_type')}
              />

              <Select
                label="Grading mode"
                data={[
                  { value: 'auto', label: 'Auto-graded' },
                  { value: 'manual', label: 'Manual-graded' },
                ]}
                {...form.getInputProps('grading_mode')}
              />
            </Group>

            <Group grow align="flex-start">
              <NumberInput label="Base XP" min={1} max={500} {...form.getInputProps('base_xp')} />
              <TextInput type="date" label="Due date" {...form.getInputProps('due_date')} />
            </Group>
          </Stack>
        </Paper>

        <Paper shadow="sm" radius="md" p="xl">
          <Stack gap={16}>
            <div>
              <Title order={4} c="var(--sat-text)">Random Question Selection</Title>
              <Text size="sm" c="var(--sat-text-soft)">
                Choose the SAT area and how many questions to generate. The system will randomly build the assignment.
              </Text>
            </div>

            <Group grow align="flex-start">
              <Select
                label="Subject"
                placeholder="Select Subject"
                data={portions.map((portion) => ({ value: String(portion.id), label: portion.name }))}
                value={form.values.portion_id}
                onChange={handlePortionChange}
                error={form.errors.portion_id}
                required
              />

              <Select
                label="Domain"
                placeholder={form.values.portion_id ? 'Select domain (optional)' : 'Choose portion first'}
                data={domains.map((domain) => ({ value: String(domain.id), label: domain.name }))}
                value={form.values.domain_id}
                onChange={handleDomainChange}
                error={form.errors.domain_id}
                disabled={!form.values.portion_id}
                clearable
              />
            </Group>

            <Group grow align="flex-start">
              <Select
                label="Mastery"
                placeholder={form.values.domain_id ? 'Select mastery (optional)' : 'Choose domain first'}
                data={masteries.map((mastery) => ({ value: String(mastery.id), label: mastery.name }))}
                value={form.values.mastery_id}
                onChange={handleMasteryChange}
                error={form.errors.mastery_id}
                disabled={!form.values.domain_id}
                clearable
              />

              <Select
                label="Difficulty"
                data={[
                  { value: '1', label: 'Level 1' },
                  { value: '2', label: 'Level 2' },
                  { value: '3', label: 'Level 3' },
                  { value: '4', label: 'Level 4' },
                  { value: '5', label: 'Level 5' },
                ]}
                {...form.getInputProps('difficulty_level')}
                clearable
              />
            </Group>

            <NumberInput
              label="Number of questions"
              description={
                !form.values.mastery_id
                  ? 'Questions will be selected randomly from the chosen portion/domain.'
                  : loadingQuestions
                    ? 'Loading available questions...'
                    : `${availableQuestionCount} question(s) available for this mastery and difficulty.`
              }
              min={1}
              max={form.values.mastery_id && availableQuestionCount > 0 ? availableQuestionCount : 100}
              {...form.getInputProps('question_count')}
            />

            {form.values.mastery_id && !loadingQuestions && availableQuestionCount === 0 && (
              <Alert color="yellow">
                No questions match this mastery and difficulty yet. Choose another difficulty level or add more questions first.
              </Alert>
            )}
          </Stack>
        </Paper>

        <Paper shadow="xs" radius="md" p="lg" style={{ background: 'var(--sat-bg)' }}>
          <Stack gap={6}>
            <Text fw={700} c="var(--sat-text)">Summary</Text>
            <Text size="sm" c="var(--sat-text-soft)">Subject: {selectedPortionName}</Text>
            <Text size="sm" c="var(--sat-text-soft)">Domain: {selectedDomainName}</Text>
            <Text size="sm" c="var(--sat-text-soft)">Mastery: {selectedMasteryName}</Text>
            <Text size="sm" c="var(--sat-text-soft)">Difficulty: {form.values.difficulty_level ? `Level ${form.values.difficulty_level}` : 'Any'}</Text>
            <Text size="sm" c="var(--sat-text-soft)">Questions: {form.values.question_count}</Text>
            <Text size="sm" c="var(--sat-text-soft)">Available matches: {availableQuestionCount}</Text>
            <Text size="sm" c="var(--sat-text-soft)">
              Grading: {form.values.grading_mode === 'auto' ? 'Auto-graded' : 'Manual-graded'}
            </Text>
          </Stack>
        </Paper>

        {!isInsertMode && (
          <Alert color="blue">
            After creation, the assignment starts as <strong>unassigned</strong>. You will choose a roster or selected students on the next screen.
          </Alert>
        )}

        <Group justify="space-between">
          <Button variant="subtle" color="gray" onClick={() => navigate(cancelHref)}>
            Cancel
          </Button>
          <Button
            color="grapefruit"
            loading={submitting}
            disabled={!!form.values.mastery_id && availableQuestionCount === 0}
            onClick={handleSubmit}
          >
            {isInsertMode ? 'Create & Insert as Session' : 'Create Assignment'}
          </Button>
        </Group>
      </Stack>
    </Container>
  )
}
